import { Image, Progress } from "@heroui/react";
import clsx from "clsx";
import { APP_CLINIC, DOCUMENT_PRIVACY_POLICY } from "core/consts";
import { getStaticAsset } from "core/model/config";
import { isDemo } from "core/model/utils/featureFlags";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { getRecareLogoOnlyPath } from "ds_legacy/components/RecareLogo";
import { Link } from "ds/ui";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { loginPage, LoginPageVariants } from "dsl/ecosystems/LoginPage/classes";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import { useTranslations } from "translations";

export type LoadingPageProps = LoginPageVariants;

export function LoadingPage({ app = APP_CLINIC, env }: LoadingPageProps) {
  const classes = loginPage({ app, env });
  const translations = useTranslations();
  const { getDocumentUrl } = useLegalDocuments();
  const externalLinkLabel = useExternalLinkLabel();

  const firstElementRef = useFocusElement<HTMLDivElement>({
    setTabIndex: true,
    shouldFocus: true,
  });

  return (
    <div className={classes.page()}>
      <div className={clsx(classes.container(), "min-h-fit")}>
        <div className={classes.content()}>
          <div className={clsx(classes.header(), "h-16 self-center")}>
            <Image
              alt="Recare logo"
              height={64}
              src={getStaticAsset(getRecareLogoOnlyPath("master"))}
              width={64}
            />
          </div>
          <h1 className={clsx(classes.title(), "text-center font-semibold")}>
            {isDemo && (
              <span className="font-normal">
                {translations.login.loginPage.titleDemo} |{" "}
              </span>
            )}
            {translations.login.loading.settingEnvironment}
          </h1>
          <Progress
            ref={firstElementRef}
            aria-label={translations.general.loading}
            aria-live="polite"
            isIndeterminate
            size="sm"
          />
          <div className="flex justify-center">
            <p className="w-4/5 text-center text-sm font-light">
              {translations.login.loading.dontLeavePage}
            </p>
          </div>
        </div>
        <div className={classes.footer()}>
          <p>
            {translations.login.loginPage.privacyPolicyInfo}{" "}
            <Link
              aria-label={externalLinkLabel(
                translations.login.privacyPolicy,
                "pdf",
              )}
              color="primary-dark"
              href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
              id="privacy-policy"
              isExternal
              size="sm"
            >
              {translations.login.privacyPolicy}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
